import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useUser } from "@/apis/bff"
import { Badge } from "@/components/ui/badge"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { EpisodeOfCareActions } from "@/modules/patients/detail/episode-of-care/components/episode-of-care-actions"
import { getEpisodeOfCareProgressNotesConnectionId } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-connection"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { EpisodeOfCareNode } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { getIsProgressNoteEditable } from "@/modules/patients/detail/progress-note/lib/get-is-progress-note-editable"
import { getProgressNoteKind } from "@/modules/patients/detail/progress-note/lib/get-progress-note-kind"
import { useEditEpisodeOfCareDialogStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareDialogStore"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useAppointmentParam, useIsInContextOfAppointment } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { formatEpisodeOfCareDuration } from "@/modules/patients/libs/format-episode-of-care-duration"
import { Ch_createProgressNoteInput } from "@/modules/patients/services/ch/__generated__/chCreateProgressNoteMutation.graphql"
import { createProgressNote } from "@/modules/patients/services/createProgressNote"
import { De_createProgressNoteInput } from "@/modules/patients/services/de/__generated__/deCreateProgressNoteMutation.graphql"

interface EpisodeOfCareItemWrapperProps {
  episodeOfCare: EpisodeOfCareNode
  patientId: string
  isEditMode: boolean
}

export const EpisodeOfCareItemWrapper = ({ episodeOfCare, patientId, isEditMode }: EpisodeOfCareItemWrapperProps) => {
  const setSelectedEpisodeOfCare = useEpisodeOfCareStoreContext((state) => state.setEpisodeOfCareFragmentReference)
  const selectedEpisodeOfCareFragmentReference = useEpisodeOfCareStoreContext(
    (state) => state.episodeOfCareFragmentReference,
  )
  const countryCode = useCountryParam()
  const selectedEpisodeOfCare = useFragment(
    getEpisodeOfCareFragment(countryCode),
    selectedEpisodeOfCareFragmentReference,
  )
  const { userName } = useUser()
  const isInContextOfAppointment = useIsInContextOfAppointment()

  const appointmentId = useAppointmentParam()
  const contextAppointment = useContextAppointment()

  const progressNotes = episodeOfCare.progressNotes?.edges
  const isEveryNoteNotEditable = progressNotes?.every(
    ({ node }) => !getIsProgressNoteEditable(node, userName, appointmentId, isInContextOfAppointment),
  )

  const canAddProgressNote = !progressNotes || isEveryNoteNotEditable

  const setEpisodeOfCareRef = useEditEpisodeOfCareDialogStoreContext((state) => state.setEpisodeOfCareRef)

  const createProgressNoteHandler = () => {
    if (contextAppointment) {
      const kind = getProgressNoteKind(contextAppointment.service)
      const input: De_createProgressNoteInput | Ch_createProgressNoteInput = {
        appointmentId: contextAppointment.id,
        patientId: patientId,
        episodeOfCareId: episodeOfCare.id,
        kind,
        language: contextAppointment.language,
      }
      const connectionId = getEpisodeOfCareProgressNotesConnectionId(episodeOfCare.id)
      createProgressNote(countryCode, input, [connectionId])
    }
  }

  const progressNoteKind = contextAppointment ? getProgressNoteKind(contextAppointment.service) : null
  return (
    <EpisodeOfCareItem
      onClick={() => setSelectedEpisodeOfCare(episodeOfCare)}
      key={episodeOfCare.id}
      episodeOfCare={episodeOfCare}
      selected={episodeOfCare.id === selectedEpisodeOfCare?.id}
      isEditMode={isEditMode}>
      {isEditMode &&
        episodeOfCare.isUpdatable &&
        progressNoteKind === "MEDICAL" &&
        contextAppointment?.isCompleted === false && (
          <EpisodeOfCareActions
            disabled={!canAddProgressNote}
            onCreateProgressNote={createProgressNoteHandler}
            onEdit={() => setEpisodeOfCareRef(episodeOfCare)}
            className="hidden group-hover/ep:inline-flex"
          />
        )}
    </EpisodeOfCareItem>
  )
}

interface EpisodeOfCareItemProps extends React.ComponentPropsWithoutRef<"div"> {
  episodeOfCare: EpisodeOfCareNode
  selected?: boolean
  isEditMode: boolean
}

const EpisodeOfCareItem = ({
  episodeOfCare,
  isEditMode,
  children,
  selected = false,
  className,
  ...itemProps
}: EpisodeOfCareItemProps) => {
  const translation = useTranslation()

  const {
    id,
    icdDiagnosis: { text, diagnosisCode: episodeOfCareIcdCode },
    updatedAt,
    progressNotes,
  } = episodeOfCare
  const isInContextOfAppointment = useIsInContextOfAppointment()
  const contextAppointment = useContextAppointment()

  return (
    <div
      {...itemProps}
      id={id}
      data-testid={`episode-of-care-${episodeOfCare.icdDiagnosis.diagnosisCode}`}
      className={cn(
        "group/ep grid-cols-1fr-auto text-on-surface-subdued hover:bg-surface-hovered focus-visible:outline-primary-focused active:bg-primary-surface-focused grid h-12 cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 transition-all focus-visible:outline-offset-2",
        selected && "bg-primary-surface text-on-primary-surface! hover:bg-primary-surface-hovered",
        className,
      )}>
      <TooltipCustom>
        <TooltipTrigger asChild>
          <div className="grid">
            <div className={cn("text-on-surface truncate text-sm", selected && "text-on-primary-surface!")}>{text}</div>
            <div className={cn("truncate text-xs leading-4")}>
              {formatEpisodeOfCareDuration(new Date(updatedAt), translation)}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="bottom" align="start" className="max-w-75" alignOffset={-8}>
            {`${episodeOfCareIcdCode} - ${text}`}
          </TooltipContent>
        </TooltipPortal>
      </TooltipCustom>
      <Badge
        className={cn(
          "bg-surface-subdued text-on-surface-subdued h-5 min-w-5 justify-center border-none p-1 leading-4 font-normal",
          isEditMode &&
            isInContextOfAppointment &&
            contextAppointment?.isCompleted === false &&
            "group-hover/ep:hidden",
        )}
        data-testid={`episode-of-care-count-for-${episodeOfCare.icdDiagnosis.diagnosisCode}`}>
        {progressNotes?.edges?.length}
      </Badge>
      {children}
    </div>
  )
}
